import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { QuestionImageViewerComponent } from './question-image-viewer.component';

@Component({
  selector: 'dojo-question-image-form-viewer',
  template: `
    <dojo-question-image-viewer
      [questionImage]="state.questionImage"
      [fillInTheBlanks]="state.fillInTheBlanksValue"
      class="flex w-full"
    />
  `,
  imports: [QuestionImageViewerComponent],
  host: {
    class: 'flex w-full gap-2 min-h-8',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionImageFormViewerComponent {
  readonly formCtrl = input.required<ImageContentItemFormGroup>();

  readonly fillInTheBlanksValue$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) =>
      formCtrl.controls.fill_in_the_blanks
        ? formCtrl.controls.fill_in_the_blanks.valueChanges.pipe(startWith(formCtrl.controls.fill_in_the_blanks.value))
        : of(false),
    ),
    map((value) => Boolean(value)),
  );

  readonly questionImage$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) =>
      combineLatest([
        formCtrl.controls.content.valueChanges.pipe(startWith(formCtrl.controls.content.value)),
        formCtrl.controls.description.valueChanges.pipe(startWith(formCtrl.controls.description.value)),
        formCtrl.controls.title.valueChanges.pipe(startWith(formCtrl.controls.title.value)),
        formCtrl.controls.not_to_scale.valueChanges.pipe(startWith(formCtrl.controls.not_to_scale.value)),
        formCtrl.controls.style.valueChanges.pipe(startWith(formCtrl.controls.style.value)),
        formCtrl.controls.container_width.valueChanges.pipe(startWith(formCtrl.controls.container_width.value)),
      ]).pipe(
        map(([content, description, title, not_to_scale, style, container_width]) => ({
          content: {
            url: content?.url ?? null,
            original_url: content?.original_url ?? null,
            code: content?.code ?? null,
            generation_completed: content?.generation_completed ?? true,
          },
          description,
          title,
          not_to_scale: not_to_scale ?? undefined,
          style,
          container_width,
        })),
      ),
    ),
  );

  readonly state = connectState({
    questionImage: this.questionImage$,
    fillInTheBlanksValue: this.fillInTheBlanksValue$,
  });
}
